import type { Platform } from './types.ts'

const DRAFT_PREFIX = 'ctcells:draft:'

/**
 * 브라우저 구현. 포트는 비동기 계약이라 데스크톱 셸 구현으로 갈아끼워도 호출부가 그대로다.
 * (동기 API 라도 Promise 로 맞춘다)
 */
export const browserPlatform: Platform = {
  kind: 'browser',

  files: {
    save({ fileName, mimeType, data }) {
      const blob = typeof data === 'string' ? new Blob([data], { type: mimeType }) : data
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = fileName
      anchor.style.display = 'none'
      /*
       * ⚠ **붙였다 떼고, URL 은 한 박자 뒤에 푼다**(2026-10-02 · 이벤트 로그 내려받기를 붙이며).
       *   DOM 밖 링크의 `click()` 은 옛 Firefox · 일부 WebKit 에서 무시되고, 같은 틱에 `revokeObjectURL` 하면
       *   내려받기가 시작되기 전에 자료가 사라질 수 있다.
       */
      document.body.append(anchor)
      anchor.click()
      anchor.remove()
      setTimeout(() => URL.revokeObjectURL(url), 0)
      return Promise.resolve()
    },
    /**
     * 🔴 **`showOpenFilePicker` 를 쓰지 않는다.** Chromium 전용이라 Firefox·Safari·WebKitGTK
     *    (= Tauri 의 리눅스 WebView)에서 없다. 숨은 `<input type="file">` 이 어디서나 선다.
     * ⚠ **`cancel` 이벤트를 같이 듣는다.** 그것만으로는 취소가 영영 안 풀려 `await` 가 매달린다
     *   (호출부는 「요청 중」 표시를 걸어 두므로 매달리면 버튼이 영구 비활성이 된다).
     * ⚠ 입력 원소를 DOM 에 붙였다가 반드시 뗀다 — 안 떼면 고를 때마다 하나씩 쌓인다.
     */
    pickText(request) {
      return new Promise((resolve, reject) => {
        const input = document.createElement('input')
        input.type = 'file'
        if (request?.accept !== undefined) input.accept = request.accept
        input.style.display = 'none'

        const done = (settle: () => void) => {
          input.remove()
          settle()
        }
        input.addEventListener('cancel', () => done(() => resolve(null)))
        input.addEventListener('change', () => {
          const file = input.files?.[0]
          if (file === undefined) {
            done(() => resolve(null))
            return
          }
          file.text().then(
            (text) => done(() => resolve({ name: file.name, text })),
            (error: unknown) => done(() => reject(error instanceof Error ? error : new Error())),
          )
        })

        document.body.append(input)
        input.click()
      })
    },
    writeDraft(key, value) {
      window.localStorage.setItem(DRAFT_PREFIX + key, value)
      return Promise.resolve()
    },
    readDraft(key) {
      return Promise.resolve(window.localStorage.getItem(DRAFT_PREFIX + key))
    },
    removeDraft(key) {
      window.localStorage.removeItem(DRAFT_PREFIX + key)
      return Promise.resolve()
    },
  },

  notifications: {
    notify({ title, body }) {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, body === undefined ? {} : { body })
      }
      return Promise.resolve()
    },
    async requestPermission() {
      if (!('Notification' in window)) return false
      if (Notification.permission === 'granted') return true
      return (await Notification.requestPermission()) === 'granted'
    },
  },

  windows: {
    open({ path, id }) {
      window.open(path, id)
      return Promise.resolve()
    },
  },

  clipboard: {
    /**
     * ⚠ **`navigator.clipboard` 는 보안 오리진에서만 «있다».** http 로 연 개발/현장 단말에서는
     *   객체 자체가 없다 — 그래서 옵셔널 체이닝이 아니라 존재를 먼저 판정한다.
     *   판정이 거짓이면 화면이 버튼을 비활성 + 사유로 세운다(조용히 실패하지 않는다).
     * 🔴 **`document.execCommand('copy')` 대체를 넣지 마라.** 폐기된 API 이고, 보이지 않는
     *    textarea 를 만들어 선택을 가로채는 방식이라 포커스가 튄다. 못 하면 못 한다고 말한다.
     */
    isAvailable() {
      return typeof navigator !== 'undefined' && navigator.clipboard !== undefined
    },
    writeText(text) {
      if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
        return Promise.reject(new Error('clipboard unavailable'))
      }
      return navigator.clipboard.writeText(text)
    },
  },

  http: {
    /**
     * 브라우저 구현. dev 는 Vite 프록시(`/api`)를 타서 CORS 를 우회한다.
     * ⚠ 데스크톱 셸에서는 이 구현이 CORS 에 막힌다 — 그때 plugin-http 구현으로 갈아끼운다.
     */
    fetch(input, init) {
      return globalThis.fetch(input, init)
    },
  },
}
